import useMemberTasks from 'services/memberTasks/queries/useMemberTasks';
import { TaskStatuses } from 'modules/Tasks/types';

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

const FORMATION_APPLICATION_TASK = 'complete_application';
const TAKEOVER_APPLICATION_TASK = 'complete_takeover_application';

interface DashboardLandingDelayOptions {
    enableFormation?: boolean;
    enableTakeover?: boolean;
}

/**
 * Checks if 3 days have passed since the user first landed on the dashboard.
 * Completing the application task is what moves a member to the launchpad, so its completed_at is the
 * landing time.
 */
export default function useDashboardLandingDelay(options: DashboardLandingDelayOptions = {}) {
    const { enableFormation = false, enableTakeover = false } = options;

    let applicationTask: string | null = null;
    if (enableFormation) {
        applicationTask = FORMATION_APPLICATION_TASK;
    } else if (enableTakeover) {
        applicationTask = TAKEOVER_APPLICATION_TASK;
    }

    const tasksQuery = useMemberTasks(
        { statuses: [TaskStatuses.COMPLETED], taskTemplateNames: applicationTask ? [applicationTask] : [] },
        { retry: false, enabled: applicationTask !== null }
    );

    const isPending = applicationTask !== null && tasksQuery.isPending;

    const landedAtMs =
        tasksQuery.data
            ?.map((task) => (task.completed_at ? Date.parse(task.completed_at) : NaN))
            .filter((ms) => !Number.isNaN(ms))
            .reduce<number | null>((latest, ms) => (latest === null || ms > latest ? ms : latest), null) ?? null;

    // If no landing time, we can't determine - hide banner to be safe
    if (applicationTask === null || landedAtMs === null) {
        return { hasThreeDaysPassed: false, isPending };
    }

    const hasThreeDaysPassed = Date.now() - landedAtMs >= THREE_DAYS_MS;

    return { hasThreeDaysPassed, isPending };
}
